import { useState, useEffect } from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect, RootStateOrAny, useDispatch } from "react-redux";
import { RouteComponentProps } from "react-router";
import moment from "moment";

import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Modal,
  Divider,
  Alert,
  Tooltip,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import QueueAnim from "rc-queue-anim";

import notifier from "utils/notifier";
import { BannerDetail, ErrorResponse } from "utils/types";
import { useLocalStorage } from "utils/hooks";
import { APCGroup, IPAData, ORGANIZATION_NAME } from "utils/objects";
import { PortalHelp } from "utils/enums";
import { setApiServer } from "constants/api";
import constants from "constants/index";

import { addCustomAppInsightsEvent } from "utils/helpers";
import {
  login,
  updateLastLoginDate,
  forgotPassword as handleForgotPassword,
  acceptOrDeclineTerms,
} from "store/auth/actions";
import { DownloadableForm, getDownloadableForms } from "store/app/actions";
import { getActiveBannerDetails } from "store/app/banner/actions";
import { getHelpCenterLinksFromPortalManager } from "store/app/helpCenter/actions";
import types from "store/auth/types";

import RegistrationDetails from "components/Login/Registration";
import ForgotPasswordModal from "components/Login/ForgotPasswordModal";
import PublicClaimStatusModal from "components/Login/PublicClaimStatusModal";
import ChangePasswordComponent from "components/UserProfile/ChangePassword";
import HelpCenterLink from "components/Common/HelpCenterLink";

import Banner from "components/Common/Banner";
import Spinner from "components/Common/Spinner";

import apolloLeft from "assets/images/apollo_left.png";
import astranaFullLogoPlum from "assets/images/Astrana_Logo_Plum.svg";
import "./style.scss";

const { Option } = Select;

type LoginPageProps = RouteComponentProps<any> & {
  login(formData: Object): any; // redux-thunk types are unnecessarily complicated
  isPostingLogin: boolean;
  updateLastLoginDate(userId: string): any;
  isFetchingAnnouncements: boolean;
  handleForgotPassword(username: string, portalName: string): any;
  forgotPassword: boolean;
  getDownloadableForms(): any; // redux-thunk types are unnecessarily complicated
  isFetchingForms: boolean;
  acceptOrDeclineTerms(accepted: boolean): any;
  getActiveBannerDetails(companyId: string): any;
  getAllDesc(): any;
  getEzcapIpasDesc(): any;
  sendRegistration(form: Object): any;
  closeAnnouncementBar(): any;
  getHelpCenterLinksFromPortalManager(page: string, type: string): any;
  isFetchingAll: boolean;
  isRegistering: boolean;
  announcementBarVisible: boolean;
};

const LoginPage = (props: LoginPageProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [selectedIPAGroup, setSelectedIPAGroup] = useState("");
  const [selectedIPA, setSelectedIPA] = useState("");
  const [rootUrl, setRootUrl] = useState("");
  const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] =
    useState(false);
  const [forms, setForms] = useState<DownloadableForm[]>([]);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [termsAccepted, setTermsAccepted] = useLocalStorage("terms", false);
  const [termsAcceptedDate, setTermsAcceptedDate] = useLocalStorage(
    "termsDate",
    null
  );
  const [registerVisible, setRegisterVisible] = useState(false);
  const [passwordResetVisible, setPasswordResetVisible] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [loginResponse, setLoginResponse] = useState<any>({});
  const [blockAccess, setBlockAccess] = useState(false);
  const [isPublicClaimStatusModalVisible, setIsPublicClaimStatusModalVisible] =
    useState(false);
  const [bannerDetails, setBannerDetails] = useState<BannerDetail[]>([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const [ipaList, setIpaList] = useState(IPAData);
  const [helpCenterLinks, setHelpCenterLinks] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const defaultGroup = "APC";

  // The help center link is in the RmiFormDownload table different from the HelpCenterLink table.
  // Only the LinkName and Url is stored in RmiFormDownload table
  const helpCenterLink = (helpCenter: any) => {
    const model = {
      ClickEventId: "Click_Help_Center_Login_Page",
      LinkName: helpCenter.LinkName,
      PagePath: "/login",
      Url: helpCenter.Link,
      CompanyId: sessionStorage.getItem("ipaGroup")!,
      User: sessionStorage.user_id,
    };
    addCustomAppInsightsEvent(model.ClickEventId, model);
  };

  useEffect(() => {
    sessionStorage.clear();
    getActiveBannerDetails();
    // temporary set LSMA and NCPN to redirect to B2C login
    filterIpaDropdownList();
    prePopulateIPADropdown();
  }, []);

  useEffect(() => {
    getHelpCenterLinks();
    if (selectedIPA !== "" && !isRedirect(selectedIPA)) {
      getDownloadableForms();
    }
    if (
      selectedIPA === constants.DISABLING_IPA_NAME &&
      moment().format("MM/DD/YYYY HH:mm") >=
        moment(constants.IPA_DISABLING_DATE_TIME).format("MM/DD/YYYY HH:mm")
    ) {
      setBlockAccess(true);
    } else {
      setBlockAccess(false);
    }
  }, [selectedIPAGroup]);

  const filterIpaDropdownList = () => {
    // filters IPA list based on B2C_IPAS environment variables (filter only if url is B2C)
    if (window.location.href.indexOf(constants.B2C_PORTAL_URL) > -1) {
      setIpaList(
        IPAData.filter((ipa) =>
          constants.B2C_IPAS?.split(",")
            .map((item: any) => item.trim())
            .includes(ipa.value)
        )
      );
    }
  };

  const isRedirect = (selectedIpa: string) => {
    return (
      constants.B2C_IPAS?.split(",")
        .map((item: any) => item.trim())
        .includes(selectedIpa) &&
      window.location.href.indexOf(constants.PORTAL_URL) > -1 &&
      !constants.B2C_TRANSITION_IPAS?.split(",") // Don't redirect if IPA is in transition phase
        .map((item: any) => item.trim())
        .includes(selectedIpa)
    );
  };

  const prePopulateIPADropdown = () => {
    //url on login page has query param of ipa found for NCPN or LSMA, then pre-populate
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const urlIPA = urlParams.get("ipa")?.toString().toUpperCase();

    if (
      urlIPA &&
      constants.B2C_IPAS?.split(",")
        .map((item: any) => item.trim())
        .includes(urlIPA)
    ) {
      setSelectedIPAGroup(urlIPA);
      setSelectedIPA(urlIPA);
      setApiServer(urlIPA);
    }
  };

  const handleLogin = () => {
    if (username !== "" && password !== "" && selectedIPAGroup !== "") {
      let formData = {
        username,
        password,
        selectedIPAGroup,
        selectedIPA,
      };

      return props
        .login(formData)
        .then((response: any) => {
          // Check password expiry
          let createdDate = new Date(response.DateCreated).valueOf();
          let expdt = new Date(response.PasswordExpirationDate).valueOf();
          let daysToExpire = Math.floor(
            (expdt - createdDate) / (60 * 60 * 24 * 1000)
          );

          if (response.PreFixPsw === 1 || daysToExpire <= 0) {
            setPasswordResetVisible(true);
            response.PreFixPsw === 1
              ? setResetReason("You must reset your password.")
              : setResetReason(
                  "Your login password has expired. Please change your password now."
                );
            setLoginResponse(response); // Save response for after password update
          } else {
            if (
              moment(response.LastLoginDate).isBefore(
                moment().startOf("day")
              ) ||
              !termsAccepted ||
              moment(termsAcceptedDate).isBefore(moment().startOf("day"))
            ) {
              setTermsAccepted(false);
              setIsTermsModalVisible(true);
              dispatch({
                type: types.LOGIN_TERMS,
                payload: response,
              });
            } else {
              setTermsAccepted(true);
              setTermsAcceptedDate(moment());
              props.updateLastLoginDate(response.UserId);
              dispatch({
                type: types.LOGIN_SUCCESS,
                payload: response,
              });
            }
          }
        })
        .catch((error: ErrorResponse) => {
          notifier.error(
            error.response?.data.Message || "Username or password is incorrect."
          );
        });
    } else {
      notifier.error("Please enter username, password, and select IPA Group.");
    }
  };

  const getActiveBannerDetails = () => {
    return props
      .getActiveBannerDetails(sessionStorage.getItem("ipaGroup")!)
      .then((response: BannerDetail[]) => {
        setBannerDetails(response);
      })
      .catch((err: ErrorResponse) => {
        console.error(
          `errorMessage=${err?.response?.data}, errorType=${err?.response?.status}:${err?.response?.statusText}`
        );
      });
  };

  const handleForgotPasswordModalClose = () => {
    setIsForgotPasswordModalVisible(false);
  };

  const handleForgotPasswordModalOpen = () => {
    setIsForgotPasswordModalVisible(true);
  };

  const handleSelectChange = (ipa: string) => {
    if (isRedirect(ipa)) {
      setShowSpinner(true);
      setTimeout(() => {
        window.location.href = `${constants.B2C_PORTAL_URL}?ipa=${ipa}`;
      }, 5000);
    }

    setApiServer(ipa);
    if (APCGroup.includes(ipa)) {
      setSelectedIPAGroup(defaultGroup);
      setSelectedIPA(ipa);
    } else {
      setSelectedIPAGroup(ipa);
      setSelectedIPA(ipa);
    }
  };

  const getDownloadableForms = () => {
    // privacy and discriminatory
    setRootUrl(sessionStorage.getItem("RESOURCE_ROOT")!);

    return props
      .getDownloadableForms()
      .then((response: DownloadableForm[]) => {
        setForms(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setTermsAcceptedDate(moment());
    setIsTermsModalVisible(false);

    let formData = {
      username,
      password,
      selectedIPAGroup,
      selectedIPA,
    };
    return props
      .login(formData)
      .then((response: any) => {
        props.updateLastLoginDate(response.UserId);
        dispatch({
          type: types.LOGIN_SUCCESS,
          payload: response,
        });
      })
      .catch((error: ErrorResponse) => {
        notifier.error(
          error.response?.data.Message || "Username or password is incorrect."
        );
      });
  };

  const handleDeclineTerms = () => {
    setTermsAccepted(false);
    setIsTermsModalVisible(false);
  };

  const handlePasswordChange = () => {
    setPasswordResetVisible(false);
    props.updateLastLoginDate(loginResponse.UserId);
    dispatch({
      type: types.LOGIN_SUCCESS,
      payload: loginResponse,
    });
  };

  const isPublicClaimStatusModalVisibleHandler = () => {
    setIsPublicClaimStatusModalVisible(false);
  };

  const onClickRedirect = () => {
    setShowSpinner(true);
    setTimeout(() => {
      window.location.href = `${constants.B2C_PORTAL_URL}?ipa=${selectedIPA}`;
    }, 2000);
  };

  const renderB2CTransition = () => {
    const isB2CTransition = constants.B2C_TRANSITION_IPAS?.split(",") // Don't redirect if IPA is in transition phase
      .map((item: any) => item.trim())
      .includes(selectedIPA);
    if (selectedIPA && isB2CTransition) {
      return (
        <>
          <Divider key="divider" />
          <div className="b2c-redirect-container">
            <Card className="blue-card" onClick={onClickRedirect} hoverable>
              <p className="redirect-text">
                Are you a User Manager looking to invite users or a user that
                has been granted full access to our enhanced provider portal?
                Click <span className="here-text">here</span>
              </p>
            </Card>
          </div>
        </>
      );
    }
  };

  const getHelpCenterLinks = () => {
    props
      .getHelpCenterLinksFromPortalManager("/login", "page")
      .then((response: any) => {
        setHelpCenterLinks(response);
      })
      .catch((err: ErrorResponse) => {
        console.log(err?.response?.data?.Message);
      });
  };

  return (
    <>
      {bannerDetails && (
        <Banner
          bannerDetails={bannerDetails}
          pagePath={props.location.pathname}
        />
      )}

      <div className="flex-container">
        <div className="flex-item-right">
          <Card
            className="login-form-card"
            style={{ height: "100%", paddingTop: "60px" }}
          >
            <div
              className="mt-3"
              style={{
                textAlign: "right",
                fontSize: "20px",
                marginRight: "20px",
              }}
              key="links"
            >
              {helpCenterLinks.map((helpCenter, index) => {
                return (
                  <a
                    href={helpCenter.link}
                    target="_blank"
                    onClick={() => helpCenterLink(helpCenter)}
                  >
                    <QuestionCircleOutlined /> {helpCenter.linkName}
                  </a>
                );
              })}
            </div>
            <div
              className="logo-section"
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                className="login-logo mb-5 mt-3"
                src={astranaFullLogoPlum}
                alt="NMM"
                style={{
                  width: "100%",
                  padding: "0 40px",
                  marginRight: "10px",
                }}
              />
              <span
                className="provider-portal"
                style={{ color: "#6c0c46", verticalAlign: "middle" }}
              >
                Provider Portal
              </span>
            </div>

            <div className="login-form-body login-form mt-3">
              <Form.Item>
                <span className="login-span">IPA</span>
                <Select
                  className="mt-2"
                  onSelect={handleSelectChange}
                  placeholder="Select IPA Group..."
                  size={"large"}
                  value={selectedIPAGroup !== "" ? selectedIPAGroup : undefined}
                >
                  {ipaList.map((e, key) => {
                    return (
                      <Option key={key} value={e.value}>
                        {e.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </div>

            <Form form={form} name={"loginForm"} onFinish={handleLogin}>
              <div className="login-form-body login-form mt-3">
                <Form.Item>
                  <span className="login-span">Username</span>
                  <Input
                    className="mt-2"
                    type="text"
                    placeholder="Username"
                    value={username}
                    disabled={selectedIPA === ""}
                    onChange={(e) => setUsername(e.currentTarget.value)}
                  />
                </Form.Item>

                <Form.Item>
                  <span className="login-span">Password</span>
                  <Input
                    className="mt-2"
                    type="password"
                    placeholder="Password"
                    value={password}
                    disabled={selectedIPA === ""}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                  />
                </Form.Item>

                <div
                  style={{
                    textAlign: "left",
                    marginBottom: "20px",
                    marginTop: "-25px",
                  }}
                  key="forgot"
                >
                  <Button
                    type="link"
                    disabled={selectedIPA === ""}
                    onClick={handleForgotPasswordModalOpen}
                    className="links forgot-password"
                  >
                    <span
                      style={{
                        color: selectedIPA === "" ? "#d3d3d3" : "#447AB8",
                      }}
                    >
                      Forgot password?
                    </span>
                  </Button>
                </div>

                <ForgotPasswordModal
                  isForgotPasswordModalVisible={isForgotPasswordModalVisible}
                  handleForgotPasswordModalClose={
                    handleForgotPasswordModalClose
                  }
                  handleForgotPasswordModalOpen={handleForgotPasswordModalOpen}
                  handleForgotPassword={props.handleForgotPassword}
                  forgotPassword={props.forgotPassword}
                  username={username}
                  setUsername={setUsername}
                  selectedIpaGroup={selectedIPAGroup}
                />

                <div className="row">
                  {window.location.href.indexOf(constants.B2C_PORTAL_URL) >
                  -1 ? (
                    <div className="col d-flex justify-content-start align-bottom">
                      <span className="links">
                        <Button
                          type="link"
                          style={{ paddingLeft: "0" }}
                          className="help-link links"
                        >
                          <Tooltip title="Redirect back to main Provider Portal">
                            <QuestionCircleOutlined
                              style={{ verticalAlign: "middle" }}
                            />
                          </Tooltip>
                          <span className="links">
                            <a href={constants.PORTAL_URL}>Other IPAs</a>
                          </span>
                        </Button>
                      </span>
                    </div>
                  ) : null}

                  <div className="col d-flex justify-content-end">
                    <Button
                      type="primary"
                      disabled={blockAccess}
                      className="login-button login-form-button waves-effect waves-light"
                      htmlType="submit"
                      loading={props.isPostingLogin}
                    >
                      Log In
                    </Button>
                  </div>
                </div>

                <div className="row">
                  <div className="col d-flex justify-content-end">
                    {!blockAccess && [
                      <div className="mt-3" key="links">
                        <QueueAnim
                          component="ul"
                          type={["right", "left"]}
                          style={{ listStyle: "none", padding: 0, margin: 0 }}
                          leaveReverse
                        >
                          <li key={"claimStatus"}>
                            <Button
                              className="claim-status-button links"
                              type="link"
                              onClick={() =>
                                setIsPublicClaimStatusModalVisible(true)
                              }
                            >
                              <span className="links">Check Claim Status</span>
                            </Button>
                          </li>
                        </QueueAnim>
                      </div>,
                    ]}
                  </div>
                </div>
              </div>
            </Form>

            {renderB2CTransition()}

            {selectedIPA !== "" &&
              !blockAccess && [
                <Divider key="divider" />,
                <div
                  className="mt-3"
                  style={{ textAlign: "center" }}
                  key="links"
                >
                  <QueueAnim
                    component="ul"
                    type={["right", "left"]}
                    style={{ listStyle: "none", padding: 0, margin: 0 }}
                    leaveReverse
                  >
                    {forms.map((form, index) => {
                      return (
                        <li key={index}>
                          {!form.RmiFileLink.includes("http") ? (
                            <a
                              href={rootUrl + form.RmiFileLink}
                              target="_blank"
                            >
                              {form.RmiFormName}
                            </a>
                          ) : null}
                          <br />
                        </li>
                      );
                    })}
                  </QueueAnim>
                  <div className="mt-2" key="forms">
                    <QueueAnim
                      delay={100}
                      component="ul"
                      type={["right", "left"]}
                      style={{ listStyle: "none", padding: 0, margin: 0 }}
                      leaveReverse
                    >
                      <li key={"privacy"}>
                        <a
                          href={
                            rootUrl +
                            "/Web/RmiPortal/NoticeOfPrivacyPractices.pdf"
                          }
                          target="_blank"
                        >
                          NOTICE OF PRIVACY PRACTICES
                        </a>
                      </li>
                      <li key={"discr"}>
                        <a
                          href={
                            rootUrl +
                            "/Web/RmiPortal/NoticeOfNondiscrimination.pdf"
                          }
                          target="_blank"
                        >
                          NOTICE OF NON DISCRIMINATION
                        </a>
                      </li>
                    </QueueAnim>
                  </div>
                </div>,
              ]}
            {blockAccess && [
              <Divider key="divider" />,
              <div className="mt-3" style={{ textAlign: "center" }} key="links">
                <p
                  dangerouslySetInnerHTML={{
                    __html: constants.IPA_DISABLING_MESSAGE,
                  }}
                />
              </div>,
            ]}

            <div className="about-section mt-3">
              <ul
                style={{
                  textAlign: "center",
                  listStyle: "none",
                  paddingLeft: "15px",
                }}
              >
                <li className="register">
                  <Button
                    type="link"
                    onClick={() => setRegisterVisible(true)}
                    className="links"
                  >
                    <span className="links">Register</span>
                  </Button>
                </li>
                <div className="about-us">About Us</div>
                <li>
                  <span className="links">
                    <a href="https://www.astranahealth.com">Our Business</a>
                  </span>
                  <span className="links">
                    <Button
                      type="link"
                      onClick={() =>
                        Modal.info({
                          title: "Portal Login Help",
                          content: PortalHelp.MESSAGE,
                          width: "750px",
                        })
                      }
                      className="help-link links"
                    >
                      <QuestionCircleOutlined />
                      <span className="links">Help</span>
                    </Button>
                  </span>
                </li>
              </ul>
              Copyright {ORGANIZATION_NAME}, Inc. © {moment().year()}
              <br />
              Build: {constants.BUILD_NUMBER}
            </div>
          </Card>
        </div>

        <Modal
          title="Password Reset"
          visible={passwordResetVisible}
          maskClosable={false}
          closable={false}
          width={1000}
          footer={[]}
        >
          <Alert
            type="warning"
            message={resetReason}
            className="mb-2"
            showIcon
          />
          <ChangePasswordComponent
            uid={
              sessionStorage.getItem("user_id") === null
                ? username
                : sessionStorage.getItem("user_id")!
            }
            setVisible={handlePasswordChange}
          />
        </Modal>

        <Modal
          title="Terms and Conditions"
          visible={isTermsModalVisible}
          okText="ACCEPT"
          cancelText="DECLINE"
          onOk={() => handleAcceptTerms()}
          onCancel={() => handleDeclineTerms()}
          maskClosable={false}
          width={1000}
        >
          <p>
            You are accessing {ORGANIZATION_NAME} information system, which
            includes
          </p>
          <ol>
            <li>this computer,</li>
            <li>this computer network,</li>
            <li>all computers connected to this network, and</li>
            <li>
              all devices and storage media attached to this network or to a
              computer on this network.
            </li>
          </ol>
          <p>
            This information system is provided for the IPA Providers managed by{" "}
            {ORGANIZATION_NAME}-authorized use only.
            <br />
            Unauthorized or improper use of this system may result in
            disciplinary action, as well as civil and criminal penalties.
            <br />
            By using this information sytem, you understand and consent to the
            following:
            <br />
          </p>
          <ul>
            <li>
              You have reasonable expectation of privacy practices regarding any
              communication or data transiting or stored on this information
              system.
            </li>
            <li>
              At any time, and for any lawful government purpose, the government
              may monitor, intercept, and search and seize any communication or
              data transiting or stored on this information system.
            </li>
            <li>
              Any communication or data transiting or stored on this information
              system may be disclosed or used for treatment, payment and health
              care operations.
            </li>
          </ul>

          <p>
            <strong>
              To continue, you must accept the terms and conditions. If you
              decline, your login will automatically be cancelled.
            </strong>
          </p>
        </Modal>

        <Modal
          title={"Web Portal Account Registration Form"}
          visible={registerVisible}
          maskClosable={false}
          closable={false}
          width={"1200px"}
          onCancel={() => setRegisterVisible(false)}
          footer={[]}
        >
          <RegistrationDetails
            setVisible={setRegisterVisible}
            selectedIpa={selectedIPA}
          />
        </Modal>

        <PublicClaimStatusModal
          isModalVisible={isPublicClaimStatusModalVisible}
          isModalVisibleHandler={isPublicClaimStatusModalVisibleHandler}
          setVisible={setIsPublicClaimStatusModalVisible}
        />
      </div>

      {showSpinner ? (
        <Spinner
          message={`You're being redirected to an enhanced Provider Portal for ${selectedIPA}`}
        />
      ) : null}
    </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isPostingLogin: store.rootReducer.auth.isPostingLogin,
  isFetchingAnnouncements: store.rootReducer.appAnnouncements.isFetchingLogin,
  forgotPassword: store.rootReducer.auth.forgotPassword,
  isFetchingForms: store.rootReducer.app.isFetchingForms,
  announcementBarVisible:
    store.persistedReducer.appAnnouncements.announcementBarVisible,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      login,
      handleForgotPassword,
      getDownloadableForms,
      acceptOrDeclineTerms,
      updateLastLoginDate,
      getActiveBannerDetails,
      getHelpCenterLinksFromPortalManager,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
